import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Info,
  Megaphone,
  PenLine,
  ShieldCheck,
  Clock,
  ListChecks,
} from 'lucide-react';
import '../CSS/GuideModal.css';

const CommunicationGuideModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <div className="gml-fab-wrapper">
        <span className="gml-fab-tooltip">Need help?</span>
        <button
          className="gml-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open Communication System Guide"
          title="Communication System Guide"
        >
          <BookOpen className="gml-fab-icon" />
        </button>
      </div>

      {/* ── Modal ── */}
      {isOpen && (
        <div
          className="gml-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gml-comm-modal-title"
        >
          <div className="gml-modal">

            {/* Header */}
            <div className="gml-modal-header">
              <div className="gml-modal-title-wrap">
                <BookOpen className="gml-modal-header-icon" />
                <h2 className="gml-modal-title" id="gml-comm-modal-title">
                  Communication Module Guide
                </h2>
              </div>
              <button
                className="gml-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="gml-modal-body">

              {/* Welcome Banner */}
              <div className="gml-welcome-banner">
                <Info className="gml-welcome-icon" />
                <div>
                  <p className="gml-welcome-heading">
                    Welcome to the Communication Module!
                  </p>
                  <p className="gml-welcome-text">
                    This guide will help you effectively manage announcements
                    and school-wide communications. Use it anytime you need
                    a quick reference.
                  </p>
                </div>
              </div>

              {/* ── Key Features Overview ── */}
              <h3 className="gml-section-heading">Key Features Overview</h3>
              <div className="gml-cards-grid">

                {/* Announcements */}
                <div className="gml-feature-card gml-feature-card--blue">
                  <div className="gml-card-head gml-card-head--blue">
                    <Megaphone className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Announcements</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">1</span>
                        Create and post school-wide announcements
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">2</span>
                        View announcement history with timestamps
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">3</span>
                        See who posted each announcement
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Editing */}
                <div className="gml-feature-card gml-feature-card--green">
                  <div className="gml-card-head gml-card-head--green">
                    <PenLine className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Editing</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">1</span>
                        Edit your existing announcements
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">2</span>
                        Real-time updates visible to all users
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">3</span>
                        Clear indication of edited content
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Management */}
                <div className="gml-feature-card gml-feature-card--yellow">
                  <div className="gml-card-head gml-card-head--yellow">
                    <ShieldCheck className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Management</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">1</span>
                        Delete outdated announcements
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">2</span>
                        Confirmation required before deletion
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">3</span>
                        Role-based access control
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Detailed Feature Guide ── */}
              <h3 className="gml-section-heading">Detailed Feature Guide</h3>
              <div className="gml-detail-section">

                {/* Posting Announcements */}
                <div className="gml-detail-card gml-detail-card--cyan">
                  <div className="gml-detail-head gml-detail-head--cyan">
                    <Clock className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Posting Announcements</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--cyan">Basic Workflow</p>
                    <ul className="gml-detail-list">
                      <li><strong>Click "Post Announcement":</strong> Opens the announcement creation form</li>
                      <li><strong>Enter Text:</strong> Type your announcement message clearly</li>
                      <li><strong>Submit:</strong> Click "Post" to publish it school-wide</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Editing Announcements</p>
                    <ul className="gml-detail-list">
                      <li><strong>Click Edit Icon:</strong> Pencil icon appears next to your announcements</li>
                      <li><strong>Modify Text:</strong> Update the announcement content as needed</li>
                      <li><strong>Save Changes:</strong> Click "Save Changes" to apply the update</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Visual Indicators</p>
                    <ul className="gml-detail-list">
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">New</span>
                        — Recently posted announcements
                      </li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">Edited</span>
                        — Announcements that have been modified
                      </li>
                      <li>Color-coded action buttons for quick identification</li>
                    </ul>
                  </div>
                </div>

                {/* Managing Announcements */}
                <div className="gml-detail-card gml-detail-card--yellow">
                  <div className="gml-detail-head gml-detail-head--yellow">
                    <ListChecks className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Managing Announcements</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--yellow">Deleting Announcements</p>
                    <ul className="gml-detail-list">
                      <li>Click the trash icon next to the announcement you want to remove</li>
                      <li>A confirmation dialog will appear before any action is taken</li>
                      <li>Confirm deletion to permanently remove the announcement</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Viewing History</p>
                    <ul className="gml-detail-list">
                      <li>All announcements are displayed in chronological order</li>
                      <li>Each entry shows the poster's name and role</li>
                      <li>Timestamps indicate exactly when each announcement was posted</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Access Control</p>
                    <ul className="gml-detail-list">
                      <li><strong>Admins</strong> can edit and delete all announcements</li>
                      <li><strong>Teachers</strong> can edit and delete their own announcements only</li>
                      <li><strong>Students</strong> have view-only access to all announcements</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Best Practices ── */}
              <div className="gml-best-practices">
                <p className="gml-best-practices-heading">Best Practices</p>
                <ul className="gml-best-practices-list">
                  {[
                    'Keep announcements concise and clear so all users can read them quickly.',
                    'Use proper formatting and punctuation for readability.',
                    'Delete outdated announcements regularly to keep the feed clean.',
                    'Double-check content before posting to avoid confusion.',
                    'Use the edit feature for minor corrections rather than deleting and reposting.',
                    'Access this guide anytime via the floating button on the bottom-right of the screen.',
                  ].map((tip, i) => (
                    <li key={i}>
                      <span className="gml-bp-dot" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="gml-modal-footer">
              <button className="gml-close-btn" onClick={() => setIsOpen(false)}>
                Got it
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default CommunicationGuideModal;